"use client";

import { meetingBusyLabel } from "@/lib/meeting-status";
import { supabase } from "@/lib/supabase";
import type { DbMeetingStatus } from "@/types/db";
import { useCallback, useEffect, useState } from "react";

type Attendee = { name: string; user_id: string | null };

export type DbProjectMeetingRecordingWithMeta = {
  id: string;
  project_id: string;
  user_id: string;
  title: string;
  created_at: string;
  status: DbMeetingStatus;
  kb_indexing_status: string;
  kb_indexed_at: string | null;
  attendees: Attendee[];
};

export type UseProjectMeetingRecordingsReturn = {
  recordings: DbProjectMeetingRecordingWithMeta[];
  loading: boolean;
  refresh: () => void;
  /** Trigger KB indexing for a project recording whose minutes are ready. */
  triggerIndexing: (recordingId: string) => Promise<{ ok: boolean; error?: string }>;
  /** Re-trigger indexing for a stuck or failed recording. */
  retryIndexing: (recordingId: string) => Promise<{ ok: boolean; error?: string }>;
  /** Detach a recording from the project — moves it back to personal meeting minutes. */
  detachRecording: (recordingId: string) => Promise<boolean>;
  /** Create a new, empty project meeting recording ready to start recording. */
  startMeeting: (
    currentUserName: string | null
  ) => Promise<{ ok: boolean; recordingId?: string; error?: string }>;
};

export function useProjectMeetingRecordings(
  projectId: string | null,
  currentUserId?: string | null,
  _canEdit?: boolean,
  personalTeamId?: string | null
): UseProjectMeetingRecordingsReturn {
  const [recordings, setRecordings] = useState<DbProjectMeetingRecordingWithMeta[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRecordings = useCallback(async () => {
    if (!projectId) {
      setRecordings([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const { data, error } = await supabase
      .from("project_meeting_recordings")
      .select("id, project_id, user_id, title, created_at, status, kb_indexing_status, kb_indexed_at, attendees")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("[useProjectMeetingRecordings] fetch error:", error);
      setRecordings([]);
      setLoading(false);
      return;
    }

    setRecordings(
      ((data as DbProjectMeetingRecordingWithMeta[] | null) ?? []).map((row) => ({
        ...row,
        attendees: Array.isArray(row.attendees) ? (row.attendees as Attendee[]) : [],
      }))
    );
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;

    queueMicrotask(() => {
      if (!cancelled) void fetchRecordings();
    });

    return () => {
      cancelled = true;
    };
  }, [fetchRecordings]);

  // Poll while any recording is in a transient indexing state, or is actively
  // recording/generating minutes — so the "wait" guard clears as soon as it's safe to move.
  const hasInProgress = recordings.some(
    (r) =>
      r.kb_indexing_status === "pending" ||
      r.kb_indexing_status === "processing" ||
      meetingBusyLabel(r.status) !== null
  );
  useEffect(() => {
    if (!hasInProgress) return;
    const id = setInterval(() => {
      // No DB polling while the tab is hidden — the visibilitychange handler
      // catches up as soon as the tab becomes visible again.
      if (document.hidden) return;
      void fetchRecordings();
    }, 3000);
    const handleVisibilityChange = () => {
      if (!document.hidden) void fetchRecordings();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [hasInProgress, fetchRecordings]);

  const triggerIndexing = useCallback(
    async (recordingId: string): Promise<{ ok: boolean; error?: string }> => {
      if (!projectId) return { ok: false, error: "No project ID" };

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch(`/api/projects/${projectId}/meeting-recordings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ recordingId }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = (body as { error?: string }).error ?? `HTTP ${res.status}`;
        console.error("[useProjectMeetingRecordings] triggerIndexing error:", msg);
        return { ok: false, error: msg };
      }

      void fetchRecordings();
      return { ok: true };
    },
    [projectId, fetchRecordings]
  );

  const retryIndexing = useCallback(
    async (recordingId: string): Promise<{ ok: boolean; error?: string }> => {
      if (!projectId) return { ok: false, error: "No project ID" };

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch(`/api/projects/${projectId}/meeting-recordings`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ recordingId }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = (body as { error?: string }).error ?? `HTTP ${res.status}`;
        return { ok: false, error: msg };
      }

      void fetchRecordings();
      return { ok: true };
    },
    [projectId, fetchRecordings]
  );

  const detachRecording = useCallback(
    async (recordingId: string): Promise<boolean> => {
      if (!projectId) return false;

      // Re-check freshness at submit time — recording/minutes generation may
      // have started after the caller last read this row's status.
      const row = recordings.find((r) => r.id === recordingId);
      if (row && meetingBusyLabel(row.status) !== null) {
        return false;
      }

      // The move (insert into meeting_recordings under the recording's own
      // owner + KB cleanup + delete from project_meeting_recordings) happens
      // atomically server-side — see detach_project_meeting_recording. It
      // always lands the meeting back on its real owner's personal space,
      // regardless of who (owner or project owner) calls this.
      const { error } = await supabase.rpc("detach_project_meeting_recording", {
        p_recording_id: recordingId,
      });

      if (error) {
        console.error("[useProjectMeetingRecordings] detachRecording error:", error);
        return false;
      }

      setRecordings((current) => current.filter((r) => r.id !== recordingId));
      return true;
    },
    [projectId, recordings]
  );

  const startMeeting = useCallback(
    async (
      currentUserName: string | null
    ): Promise<{ ok: boolean; recordingId?: string; error?: string }> => {
      if (!projectId || !currentUserId || !personalTeamId) {
        return { ok: false, error: "Missing project, user, or workspace context." };
      }

      const now = new Date();
      const datePart = new Intl.DateTimeFormat(undefined, {
        year: "numeric", month: "short", day: "2-digit",
      }).format(now);
      const timeParts = new Intl.DateTimeFormat(undefined, {
        hour: "2-digit", hour12: true,
      }).formatToParts(now);
      const hourPart = timeParts.find((p) => p.type === "hour")?.value ?? "";
      const dayPeriod = timeParts.find((p) => p.type === "dayPeriod")?.value?.toUpperCase() ?? "";
      const timestamp = `${datePart} ${[hourPart, dayPeriod].filter(Boolean).join(" ")}`.trim();

      const { data: created, error } = await supabase
        .from("project_meeting_recordings")
        .insert({
          user_id: currentUserId,
          project_id: projectId,
          team_id: personalTeamId,
          title: `New Meeting ${timestamp}`.slice(0, 200),
          meeting_context: "",
          transcript: "",
          minutes: "",
          attendees: [{ name: currentUserName ?? "You", user_id: currentUserId }],
          duration_seconds: 0,
          transcribed_chunks: 0,
          failed_chunks: 0,
          status: "RECORDING_NOT_STARTED",
        })
        .select("id")
        .single();

      if (error || !created) {
        console.error("[useProjectMeetingRecordings] startMeeting error:", error);
        return { ok: false, error: error?.message ?? "Failed to create meeting." };
      }

      return { ok: true, recordingId: created.id };
    },
    [projectId, currentUserId, personalTeamId]
  );

  return {
    recordings,
    loading,
    refresh: fetchRecordings,
    triggerIndexing,
    retryIndexing,
    detachRecording,
    startMeeting,
  };
}
